import { useEffect, useRef, useState } from 'react';
import { useConnection } from '../lib/ConnectionProvider';
import {
  FALLBACK_POLL_BASE_MS,
  FALLBACK_POLL_JITTER_MS,
  nextDelay,
  shouldPoll,
} from './fallbackPoll';
import type { SocketStatus } from './useReconnectingSocket';

/**
 * Richiama `poll` a intervalli regolari finché il websocket non è aperto.
 *
 * È il ripiego descritto in `fallbackPoll.ts`: qui ci sono solo i timer e il
 * ciclo di vita. Tre cose a cui fare attenzione, perché sbagliarle si vede solo
 * sul campo:
 *
 * - **un giro alla volta.** Il successivo si programma quando il precedente è
 *   finito, quindi due richieste non si accavallano mai e una risposta lenta
 *   non fa partire una richiesta in più.
 * - **primo giro immediato.** Quando entra in funzione — all'apertura, al
 *   ritorno della rete, al risveglio della scheda — non c'è motivo di aspettare
 *   un intervallo intero per accorgersi di un cambiamento.
 * - **sospeso a scheda nascosta.** Un tablet nel taschino non deve consumare
 *   traffico; al ritorno il giro riparte subito, quindi non si perde niente.
 */
export function useFallbackPoll(options: {
  /** Falso quando la schermata non ha nulla da aggiornare (o non è ancora pronta). */
  active: boolean;
  status: SocketStatus;
  poll: () => Promise<void>;
}): void {
  const { active, status, poll } = options;
  const { online } = useConnection();

  const [visible, setVisible] = useState(() => document.visibilityState !== 'hidden');

  useEffect(() => {
    function onChange() {
      setVisible(document.visibilityState !== 'hidden');
    }

    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);

  // La richiamata cambia a ogni render: tenerla in una ref evita che un
  // ridisegno faccia ripartire il ciclo (e il giro immediato) da capo.
  const pollRef = useRef(poll);
  pollRef.current = poll;

  const running = shouldPoll({ status, active, online, visible });

  useEffect(() => {
    if (!running) return;

    let cancelled = false;
    let timer: number | undefined;
    /** Inizio del giro precedente: da lì si misura l'attesa del prossimo. */
    let startedAt = Date.now();

    async function round(): Promise<void> {
      startedAt = Date.now();

      try {
        await pollRef.current();
      } catch {
        // Un giro perso non è un guasto da mostrare: i dati che ci sono
        // restano validi finché il server non risponde di nuovo, e il
        // prossimo giro è già programmato.
      }

      if (cancelled) return;

      const interval = FALLBACK_POLL_BASE_MS + Math.random() * FALLBACK_POLL_JITTER_MS;
      timer = window.setTimeout(() => void round(), nextDelay(startedAt, Date.now(), interval));
    }

    void round();

    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [running]);
}
